import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { Box, Card, Link, ListItemText, SingleListItem, Skeleton, Stack } from '@dhl-ui/components'
import { CheckboxInput, TextFieldInput } from '@dhl/formik'
import { AgreedPlacePhotoUploadInput } from '@dhl/interventions'

import { isFeatureEnabled } from '~/config/index.js'
import { AgreedPlaceAdditionalInformation } from './AgreedPlaceAdditionalInformation.js'

const PrivacyPolicy = () => {
  const { t } = useTranslation()

  return (
    <Link external href={t('inputs.agreedPlaceInput.privacyPolicyUrl')} rel="noreferrer" target="_blank">
      {t('inputs.agreedPlaceInput.privacyPolicyText')}
    </Link>
  )
}

const TermsConditions = () => {
  const { t } = useTranslation()

  return (
    <Link external href={t('inputs.agreedPlaceInput.termsConditionsUrl')} rel="noreferrer" target="_blank">
      {t('inputs.agreedPlaceInput.termsConditionsText')}
    </Link>
  )
}

export const useCreateAgreedPlaceValidationSchema: () => {
  validationSchema: Yup.ObjectSchema<
    {
      description: string
      termsAccepted: boolean
    },
    Yup.AnyObject,
    {
      description: undefined
      termsAccepted: false
    }
  >
} = () => {
  const { t } = useTranslation()

  const validationSchema = Yup.object({
    description: Yup.string()
      .required(t('portal.profile.preferences.options.AgreedPlaceDelivery.inputIsRequiredErrorMessage'))
      .min(5, t('portal.profile.preferences.options.AgreedPlaceDelivery.errorFieldTooShort', { min: 5 }))
      .max(80, t('portal.profile.preferences.options.AgreedPlaceDelivery.errorFieldTooLong', { max: 80 })),
    termsAccepted: Yup.boolean()
      .default(false)
      .oneOf([true], t('portal.profile.preferences.options.AgreedPlaceDelivery.termsAndConditionsRequired')),
  })

  return { validationSchema }
}

type Props = {
  isLoadingPhoto?: boolean
}

export const AgreedPlaceForm: FC<Props> = ({ isLoadingPhoto }) => {
  const { t } = useTranslation()

  return (
    <Card hasPaddingBottom hasPaddingTop>
      <Card.Header title={t('portal.profile.preferences.setupPage.agreedPlace.cardTitle')} />
      <Card.Content>
        <Box>
          <TextFieldInput
            fullWidth
            multiline
            required
            data-testid="description-input"
            label={t('portal.profile.preferences.options.AgreedPlaceDelivery.agreedPlaceLabel')}
            name="description"
            rows="3"
            slotProps={{
              htmlInput: {
                maxLength: 80,
              },
            }}
            type="textarea"
          />
        </Box>
      </Card.Content>

      {isFeatureEnabled('profile.deliveryPreferences.agreedPlacePhotoUpload') && (
        <Card.Content noPaddingSides noPaddingTop>
          <SingleListItem
            hasDivider={false}
            slotProps={{
              footer: (
                <Box px={2}>
                  {!isLoadingPhoto && <AgreedPlacePhotoUploadInput name="agreedPlacePhoto" />}
                  {isLoadingPhoto && <Skeleton variant="rectangular" width="100%" height={300} />}
                </Box>
              ),
            }}
          >
            <ListItemText
              emphasis
              primary={t('portal.profile.preferences.options.AgreedPlaceDelivery.image.title')}
              secondary={t('portal.profile.preferences.options.AgreedPlaceDelivery.image.description')}
            />
          </SingleListItem>
        </Card.Content>
      )}

      <Card.Content>
        <Stack spacing={2}>
          <AgreedPlaceAdditionalInformation />

          <CheckboxInput
            required
            label={
              <>
                {(
                  t('inputs.agreedPlaceInput.privacyAndTermsText', {
                    returnObjects: true,
                  }) as string[]
                ).map((text, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
                  <span key={index}>
                    {text} {index === 0 && <TermsConditions />} {index === 1 && <PrivacyPolicy />}
                  </span>
                ))}
              </>
            }
            name="termsAccepted"
          />
        </Stack>
      </Card.Content>
    </Card>
  )
}
